/**
 * TEMPORARY diagnostics bridge for the Google OAuth mobile scaling investigation.
 *
 * The actual collector is an inline script in `index.html` (earliest head
 * execution). This module only forwards snapshots from React lifecycle points
 * and exposes the persisted log to the debug panel. It is silent for normal
 * users and adds no UI unless `?debugViewport=1` is present.
 */

export type ViewportDiagnosticSnapshot = {
  stage: string;
  t: number;
  at: string;
  innerWidth: number;
  innerHeight: number;
  clientWidth: number | null;
  clientHeight: number | null;
  docScrollWidth: number | null;
  bodyScrollWidth: number | null;
  screenWidth: number;
  screenHeight: number;
  availWidth: number;
  availHeight: number;
  vvWidth: number | null;
  vvHeight: number | null;
  vvScale: number | null;
  dpr: number;
  md768: boolean | null;
  viewportMeta: string | null;
  viewportMetaCount: number;
  url: string;
  referrer: string | null;
  visibility: DocumentVisibilityState;
  navType: string | null;
  oauthPending: string | null;
  oauthCleanLoad: string | null;
  oauthDecision: string | null;
  extra?: Record<string, unknown>;
};

type DiagApi = {
  key: string;
  snap: (stage: string, extra?: Record<string, unknown>) => ViewportDiagnosticSnapshot;
  read: () => ViewportDiagnosticSnapshot[];
  log: ViewportDiagnosticSnapshot[];
  clear: () => void;
};

function api(): DiagApi | null {
  if (typeof window === "undefined") return null;
  return (window as Window & { __vpDiag?: DiagApi }).__vpDiag ?? null;
}

/** Records a timestamped viewport snapshot at a React lifecycle stage. */
export function vpSnap(stage: string, extra?: Record<string, unknown>): void {
  api()?.snap(stage, extra);
}

export function readViewportDiagnostics(): ViewportDiagnosticSnapshot[] {
  return api()?.read() ?? [];
}

export function clearViewportDiagnostics(): void {
  api()?.clear();
}

/** True when the visitor explicitly asked for the diagnostics panel. */
export function isViewportDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("debugViewport") === "1";
}

/** Appends `debugViewport=1` to a same-origin path when debugging is active. */
export function withDebugViewport(path: string): string {
  if (!isViewportDebugEnabled()) return path;
  const url = new URL(path, window.location.origin);
  url.searchParams.set("debugViewport", "1");
  return `${url.pathname}${url.search}${url.hash}`;
}
